<template>
  <b-overlay
    :show="show"
    spinner-variant="primary"
    spinner-type="grow"
    spinner-small
    rounded="sm"
  >
    <b-card>
      <div class="d-flex align-items-center mb-1">
        <b-button
          variant="flat-secondary"
          size="sm"
          :to="{ name: 'pamm_management_account' }"
        >
          <feather-icon
            :icon="$i18n.isRTL ? 'ArrowRightIcon' : 'ArrowLeftIcon'"
            size="14"
          />
          <span class="mx-25">{{ $t('back') }}</span>
        </b-button>
        <h4 class="card-title mb-0 mx-1">
          {{ $t('pamm_periods') }}
        </h4>
      </div>
      <b-row
        align-h="between"
        class="mb-1"
      >
        <b-col
          cols="12"
          md="8"
          class="d-flex align-items-center"
        >
          <span v-if="account.nick_name">{{ $t('pamm_periods_description', { name: account.nick_name }) }}</span>
        </b-col>
        <b-col
          cols="12"
          md="auto"
          class="mt-1 mt-md-0"
        >
          <b-button
            variant="primary"
            @click="openCreateModal"
          >
            {{ $t('pamm_period_create') }}
          </b-button>
        </b-col>
      </b-row>
      <b-table
        :items="periods"
        :fields="periodFields"
        striped
        responsive
        show-empty
        :empty-text="$t('pamm_no_periods')"
      >
        <template #cell(invest)="data">
          <div class="text-nowrap">
            {{ formatDate(data.item.invest_start_at) }}
          </div>
          <div class="text-nowrap">
            {{ formatDate(data.item.invest_end_at) }}
          </div>
        </template>
        <template #cell(trade)="data">
          <div class="text-nowrap">
            {{ formatDate(data.item.trade_start_at) }}
          </div>
          <div class="text-nowrap">
            {{ formatDate(data.item.trade_end_at) }}
          </div>
        </template>
        <template #cell(fees)="data">
          <div class="text-nowrap">
            {{ $t('fee_static') }}: {{ formatValue(data.item.fee_static, '$') }}
          </div>
          <div class="text-nowrap">
            {{ $t('fee_percent') }}: {{ formatValue(data.item.fee_percent, '%') }}
          </div>
        </template>
        <template #cell(sharing)="data">
          <div class="text-nowrap">
            {{ $t('profit_share') }}: {{ formatValue(data.item.profit_share_percent, '%') }}
          </div>
          <div class="text-nowrap">
            {{ $t('loss_share') }}: {{ formatValue(data.item.loss_share_percent, '%') }}
          </div>
        </template>
        <template #cell(cancelation)="data">
          <div class="text-nowrap">
            {{ $t('caneclation_period') }}: {{ formatValue(data.item.premature_cancelation_period, $t('day')) }}
          </div>
          <div class="text-nowrap">
            {{ $t('caneclation_fee') }}: {{ formatValue(data.item.premature_cancelation_fine_percent, '%') }}
          </div>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="statusVariants[data.item.status] || 'light-secondary'">
            {{ $te(`pamm_period_status_${data.item.status}`) ? $t(`pamm_period_status_${data.item.status}`) : data.item.status }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button
            variant="flat-primary"
            size="sm"
            class="text-nowrap"
            @click="openEditModal(data.item)"
          >
            <feather-icon
              icon="Edit2Icon"
              class="mr-50"
            />
            <span>{{ $t('pamm_period_edit') }}</span>
          </b-button>
          <b-button
            variant="flat-primary"
            size="sm"
            class="text-nowrap"
            @click="openInvestorsModal(data.item)"
          >
            <feather-icon
              icon="UsersIcon"
              class="mr-50"
            />
            <span>{{ $t('investors') }}</span>
          </b-button>
        </template>
      </b-table>
    </b-card>

    <b-modal
      v-model="investorsModalShow"
      static
      lazy
      centered
      size="xl"
      :title="$t('pamm_period_investors')"
      ok-only
      :ok-title="$t('close')"
      ok-variant="outline-secondary"
    >
      <p
        v-if="investorsPeriod"
        class="text-muted"
      >
        {{ $t('pamm_invest_window') }}:
        {{ formatDate(investorsPeriod.invest_start_at) }} - {{ formatDate(investorsPeriod.invest_end_at) }}
      </p>
      <b-overlay
        :show="investorsPending"
        spinner-variant="primary"
        spinner-type="grow"
        spinner-small
        rounded="sm"
      >
        <b-table
          :items="periodInvestors"
          :fields="periodInvestorFields"
          striped
          responsive
          show-empty
          :empty-text="investorsPending ? '' : $t('no_investors')"
        >
          <template #cell()="data">
            <span class="text-nowrap">{{ formatCell(data.field.key, data.value) }}</span>
          </template>
        </b-table>
      </b-overlay>
    </b-modal>

    <!-- static: portaled modal content loses $i18n in child components (bootstrap-vue 2.23) -->
    <b-modal
      v-model="periodModalShow"
      static
      lazy
      centered
      size="lg"
      :title="editingPeriodId ? $t('pamm_period_edit') : $t('pamm_period_create')"
      hide-footer
      no-close-on-backdrop
    >
      <b-overlay
        :show="periodPending"
        spinner-variant="primary"
        spinner-type="grow"
        spinner-small
        rounded="sm"
      >
        <validation-observer v-slot="{ invalid }">
          <b-form @submit.prevent>
            <period-form-fields v-model="period" />
            <div
              v-if="!editingPeriodId"
              class="border rounded p-1 mb-1"
            >
              <h6 class="d-flex align-items-center mb-1">
                <feather-icon
                  icon="PaperclipIcon"
                  size="16"
                  class="text-primary"
                />
                <span class="mx-50">{{ $t('pamm_period_file') }}</span>
              </h6>
              <b-form-file
                v-model="periodFile"
                accept="image/png, image/jpg, image/jpeg, application/pdf"
                :placeholder="$t('no_file_chosen')"
              />
            </div>
            <b-row>
              <b-col cols="6">
                <b-button
                  block
                  variant="outline-secondary"
                  :disabled="submitPending"
                  @click="periodModalShow = false"
                >
                  {{ $t('cancel') }}
                </b-button>
              </b-col>
              <b-col cols="6">
                <b-button
                  block
                  variant="primary"
                  :disabled="invalid || !isDateOrderValid || periodPending || submitPending"
                  @click="submitPeriod"
                >
                  <span v-if="!submitPending">
                    {{ editingPeriodId ? $t('save_changes') : $t('pamm_period_create') }}
                  </span>
                  <b-spinner
                    v-else
                    small
                    type="grow"
                  />
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-overlay>
    </b-modal>
  </b-overlay>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BBadge,
  BModal,
  BForm,
  BFormFile,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import jalaali from 'moment-jalaali'
import PammApis from '@/modules/pamm/service/apis'
import { numberWithCommas } from '@/utils/index'
import { emptyPeriod, toPeriodForm, isPeriodDateOrderValid } from '@/modules/pamm/utils/period'
import PeriodFormFields from '../components/period-form-fields.vue'

const pammApis = new PammApis()

const getResults = res => {
  const { results } = res.data
  return Array.isArray(results) ? results : (results?.data ?? [])
}

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BModal,
    BForm,
    BFormFile,
    BSpinner,
    ValidationObserver,
    PeriodFormFields,
  },
  data() {
    return {
      show: false,
      account: {},
      periods: [],
      periodModalShow: false,
      periodPending: false,
      submitPending: false,
      editingPeriodId: null,
      period: emptyPeriod(),
      periodFile: null,
      investorsModalShow: false,
      investorsPending: false,
      investorsPeriod: null,
      periodInvestors: [],
      statusVariants: {
        subscribing: 'light-primary',
        freezing: 'light-warning',
        trading: 'light-success',
        end: 'light-secondary',
      },
    }
  },
  computed: {
    periodFields() {
      return [
        { key: 'invest', label: this.$t('pamm_invest_window') },
        { key: 'trade', label: this.$t('pamm_trade_window') },
        { key: 'fees', label: this.$t('pamm_terms_fees') },
        { key: 'sharing', label: this.$t('pamm_terms_sharing') },
        { key: 'cancelation', label: this.$t('pamm_terms_cancelation') },
        { key: 'status', label: this.$t('status') },
        { key: 'actions', label: this.$t('actions') },
      ]
    },
    periodInvestorFields() {
      const [first] = this.periodInvestors
      // with no rows there is nothing to derive columns from; one column keeps the empty text visible
      if (!first) return [{ key: 'id', label: '#' }]
      return Object.keys(first)
        .filter(key => first[key] === null || typeof first[key] !== 'object')
        .map(key => ({
          key,
          label: this.$te(key) ? this.$t(key) : key.replace(/_/g, ' '),
        }))
    },
    isDateOrderValid() {
      return isPeriodDateOrderValid(this.period)
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    async getData() {
      this.show = true
      const { id } = this.$route.params
      await Promise.all([
        pammApis.getPeriods(id).then(res => {
          this.periods = getResults(res)
        }),
        pammApis.getManagerList().then(res => {
          this.account = getResults(res).find(item => String(item.id) === String(id)) ?? {}
        }),
      ]).catch(err => {
        console.log(err)
      }).finally(() => {
        this.show = false
      })
    },
    async getPeriods() {
      await pammApis.getPeriods(this.$route.params.id).then(res => {
        this.periods = getResults(res)
      }).catch(err => {
        console.log(err)
      })
    },
    openCreateModal() {
      this.editingPeriodId = null
      this.period = emptyPeriod()
      this.periodFile = null
      this.periodModalShow = true
    },
    async openEditModal(item) {
      this.editingPeriodId = item.id
      this.period = toPeriodForm(item)
      this.periodModalShow = true
      this.periodPending = true
      await pammApis.getPeriod(this.$route.params.id, item.id).then(res => {
        this.period = toPeriodForm(res.data.results ?? item)
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.periodPending = false
      })
    },
    async submitPeriod() {
      this.submitPending = true
      const { id } = this.$route.params
      const isEdit = !!this.editingPeriodId
      const request = isEdit
        ? pammApis.updatePeriod(id, this.editingPeriodId, this.period)
        : pammApis.createPeriod(id, { ...this.period, file: this.periodFile })
      await request.then(res => {
        this.periodModalShow = false
        this.$swal({
          title: res.data.message || this.$t(isEdit ? 'pamm_period_updated' : 'pamm_period_created'),
          icon: 'success',
          timer: 4000,
          showConfirmButton: false,
        })
        this.getPeriods()
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.submitPending = false
      })
    },
    async openInvestorsModal(item) {
      this.investorsPeriod = item
      this.periodInvestors = []
      this.investorsModalShow = true
      this.investorsPending = true
      await pammApis.getPeriodInvestors(this.$route.params.id, item.id).then(res => {
        this.periodInvestors = getResults(res)
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        this.investorsPending = false
      })
    },
    formatCell(key, value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'boolean') return value ? '✓' : '✗'
      if (/(_at|_date|_time)$/.test(key)) return this.formatDate(value)
      if (typeof value === 'number' && !/(^id$|_id$)/.test(key)) return numberWithCommas(value)
      return value
    },
    formatDate(date) {
      if (!date) return '-'
      return jalaali(date).format(this.$i18n.locale === 'fa' ? 'jYYYY/jMM/jDD' : 'YYYY-MM-DD')
    },
    formatValue(value, unit) {
      if (value === null || value === undefined) return '-'
      return `${numberWithCommas(value)} ${unit}`.trim()
    },
  },
}
</script>
